import axios, {
  AxiosInstance,
  Method as AxiosMethod,
  AxiosRequestConfig,
  AxiosResponse,
  ResponseType as AxiosResponseType,
} from 'axios';
import {
  GraphQLClient,
  ClientError as GraphQLClientError,
  type RequestOptions as LibGraphQLRequestOptions,
  type Variables as LibGraphQLVariables,
} from 'graphql-request';
import type { Logger } from '../logger';
import {
  ApiError,
  GraphQLApiError,
  NetworkError,
  RestApiError,
  TimeoutError,
} from './errors';
import type {
  ApiClientConfig,
  GetAuthTokenFn,
  GraphQLDocument,
  GraphQLRequestOptions,
  RawApiResponse,
  RestRequestConfig,
  StandardRestResponse,
} from './types';
import { createCombinedSignal, normalizeAxiosHeaders } from './utils';

/**
 * ApiClient is a unified HTTP client for making REST and GraphQL requests.
 * It provides a consistent, library-agnostic interface, robust error handling,
 * and configurable options for authentication, timeouts, and request cancellation.
 *
 * ## How to Use ApiClient
 *
 * 1.  **Prerequisites:**
 * * Ensure you have an implementation of the `Logger` interface (e.g., from `../logger`).
 * * Define a `GetAuthTokenFn` function if you need authenticated requests.
 *
 * 2.  **Import necessary items:**
 * ```typescript
 * import {
 * ApiClient,
 * type ApiClientConfig,
 * // ... other types and errors
 * } from './api-client'; // Adjust path to your api-client module
 * import type { Logger } from '../logger'; // Adjust path
 * // import { yourGetAuthTokenImplementation } from './auth';
 * // import { myLoggerInstance } from './logging';
 * ```
 *
 * 3.  **Define `getAuthToken` function (if using secure requests):**
 * ```typescript
 * const getMyAuthToken: GetAuthTokenFn = async () => {
 * // Example: return (await Auth.currentSession()).getIdToken().getJwtToken();
 * return 'your-actual-jwt-token-or-null';
 * };
 * ```
 *
 * 4.  **Create an `ApiClientConfig`:**
 * ```typescript
 * const apiClientConfig: ApiClientConfig = {
 * restApiBaseUrl: '[https://api.example.com/v1](https://api.example.com/v1)', // Required
 * graphqlApiBaseUrl: '[https://api.example.com/graphql](https://api.example.com/graphql)', // Required
 * logger: myLoggerInstance, // Required
 * defaultTimeout: 20000, // Optional
 * getAuthToken: getMyAuthToken, // Optional, for secure requests
 * };
 * ```
 *
 * 5.  **Instantiate `ApiClient`:**
 * ```typescript
 * const apiClient = new ApiClient(apiClientConfig);
 * ```
 * It's highly recommended to create a single instance and reuse it.
 *
 * 6.  **Making REST Requests (`rest` method):**
 * See the JSDoc for the `rest` method for detailed examples on object responses,
 * array/primitive responses (using `_rawData`), and raw responses.
 *
 * 7.  **Making GraphQL Requests (`graphql` method):**
 * See the JSDoc for the `graphql` method for detailed examples.
 *
 * ## Error Handling
 * The `ApiClient` throws custom error types extending `ApiError` (e.g., `RestApiError`,
 * `GraphQLApiError`, `TimeoutError`, `NetworkError`). Use `instanceof` for granular
 * error handling.
 *
 * ## `_rawData` Field in REST Responses
 * When `raw: false` and the API response body isn't a plain object, the `_rawData`
 * field in the returned object holds the original parsed data.
 */
export class ApiClient {
  private readonly logger: Logger;
  private readonly defaultTimeout: number;
  private readonly getAuthTokenFn: GetAuthTokenFn;
  private readonly axiosInstance: AxiosInstance;
  private readonly graphqlClient: GraphQLClient;
  readonly restBaseUrl: string;
  readonly graphqlApiBaseUrl: string;

  /**
   * Creates an instance of ApiClient.
   * @param {ApiClientConfig} config - Configuration for the ApiClient.
   * `restApiBaseUrl`, `graphqlApiBaseUrl`, and `logger` are required.
   * @throws {Error} If required configuration properties are missing.
   */
  constructor(config: ApiClientConfig) {
    if (!config.logger) {
      // Log to console as logger instance isn't available yet.
      console.error(
        'ApiClient: A Logger instance is required in the configuration.',
      );
      throw new Error(
        'ApiClient: A Logger instance is required in the configuration.',
      );
    }
    this.logger = config.logger.child('ApiClient');

    if (!config.restApiBaseUrl) {
      const errMsg =
        'ApiClient: restApiBaseUrl is required in the configuration.';
      this.logger.error(errMsg);
      throw new Error(errMsg);
    }
    this.restBaseUrl = config.restApiBaseUrl;

    if (!config.graphqlApiBaseUrl) {
      const errMsg =
        'ApiClient: graphqlApiBaseUrl is required in the configuration.';
      this.logger.error(errMsg);
      throw new Error(errMsg);
    }
    this.graphqlApiBaseUrl = config.graphqlApiBaseUrl;

    this.defaultTimeout = config.defaultTimeout ?? 30000; // Default to 30s

    this.getAuthTokenFn =
      config.getAuthToken ??
      (async () => {
        this.logger.warn(
          'ApiClient: getAuthTokenFn was not provided. Secure calls will not be authenticated.',
        );
        return null;
      });

    this.axiosInstance = axios.create({
      baseURL: this.restBaseUrl,
    });

    this.graphqlClient = new GraphQLClient(this.graphqlApiBaseUrl);
  }
  /**
   * Performs a REST request and returns a library-agnostic raw response.
   * Use this when you need access to status codes, headers, or non-JSON response bodies.
   *
   * @template TData The expected type of the raw response body (e.g., string, ArrayBuffer).
   * @param {RestRequestConfig & { raw: true }} config Configuration for the REST request, with `raw` set to true.
   * @returns {Promise<RawApiResponse<TData>>} A promise resolving to a `RawApiResponse` object.
   * @throws {RestApiError | TimeoutError | NetworkError | ApiError} Throws a custom API error on failure.
   */
  public async rest<TData = any>(
    config: RestRequestConfig & { raw: true },
  ): Promise<RawApiResponse<TData>>;

  /**
   * Performs a REST request and returns a processed response compatible with legacy customFetch.
   * This is the default behavior.
   *
   * @template TData The expected type of the data in the JSON response body.
   * @param {RestRequestConfig} config Configuration for the REST request. `raw` should be falsy.
   * @returns {Promise<StandardRestResponse<TData>>}
   * A promise resolving to an object:
   * - If TData is an object: `TData` spread with `success` and `statusCode`.
   * - If TData is not an object: `{ _rawData: TData, success: boolean, statusCode: number }`.
   * @throws {RestApiError | TimeoutError | NetworkError | ApiError} Throws a custom API error on failure.
   */
  public async rest<TData = any>(
    config: RestRequestConfig & { raw?: false | undefined },
  ): Promise<StandardRestResponse<TData>>;

  /**
   * Main implementation of the `rest` method. Do not call this directly; use one of the overloads.
   * @private
   */
  public async rest<TData = any>(
    config: RestRequestConfig,
  ): Promise<StandardRestResponse<TData> | RawApiResponse<TData>> {
    const {
      baseURL,
      path = '',
      method = 'GET',
      signal: externalSignal,
      timeout,
    } = config;

    const fullUrl = new URL(path, baseURL || this.restBaseUrl).toString();

    if (!path && !baseURL) {
      throw new RestApiError(
        'It is assumed that path = "" but it is necessary to provide baseURL',
      );
    }

    const { operationSignal, clearInternalTimeout } = this.createAbortSignal(
      externalSignal,
      timeout,
    );

    try {
      const requestHeaders = await this.prepareAuthHeaders(config);
      const axiosReqConfig: AxiosRequestConfig = {
        url: path,
        method: method as AxiosMethod,
        data: config.body,
        params: config.queryParams,
        headers: requestHeaders,
        signal: operationSignal,
        responseType: config.raw
          ? (config.responseType as AxiosResponseType) || 'text'
          : 'json',
        ...(baseURL && { baseURL }),
      };

      const response = await this.executeRestRequest<TData>(axiosReqConfig);

      if (config.raw === true) {
        return {
          status: response.status,
          data: response.data,
          headers: normalizeAxiosHeaders(response.headers),
        };
      }

      return this.processStandardRestResponse<TData>(response);
    } catch (error: any) {
      throw this.handleRestError(error, fullUrl, operationSignal);
    } finally {
      clearInternalTimeout();
    }
  }

  /**
   * Performs a GraphQL request with improved type inference using TypedDocumentNode.
   * If a plain string or a non-typed DocumentNode is passed, TResult (response data type)
   * will default to `any` unless explicitly provided by the caller.
   *
   * @template TResult The expected shape of the data returned by the GraphQL query/mutation.
   * This is often inferred if a TypedDocumentNode is provided as the `document`.
   * @template TVars The shape of the variables object for the GraphQL query/mutation.
   * This is also often inferred if a TypedDocumentNode is provided.
   * @param {GraphQLDocument<TResult, TVars>} document The GraphQL query or mutation.
   * Your `GraphQLDocument` type should be `string | LibGraphQLRequestDocument | TypedDocumentNode<TResult, TVars>`.
   * @param {TVars} [variables] An object containing variables for the GraphQL request.
   * The type should match the variables expected by the document.
   * @param {GraphQLRequestOptions} [options={}] Optional configurations for this GraphQL request.
   * @returns {Promise<TResult>} A promise that resolves to the typed data from the GraphQL response.
   * @throws {GraphQLApiError | TimeoutError | NetworkError | ApiError} Throws a custom API error on failure.
   *
   * @example
   * // Assuming GetUserDocument is a TypedDocumentNode<UserData, UserVars>
   * // const { user } = await apiClient.graphql(GetUserDocument, { id: "123" });
   * // console.log(user.name); // user and its properties are strongly typed
   *
   * // With a string query, explicit types are needed for TResult if not 'any'
   * // const { specificField } = await apiClient.graphql<{ specificField: string }>(
   * //   "query { specificField }",
   * // );
   * // console.log(specificField);
   */
  public async graphql<
    TResult = any,
    TVars extends LibGraphQLVariables = LibGraphQLVariables,
  >(
    document: GraphQLDocument<TResult, TVars>,
    variables?: TVars,
    options: GraphQLRequestOptions = {},
  ): Promise<TResult> {
    const { timeout, signal: externalSignal } = options;
    const { operationSignal, clearInternalTimeout } = this.createAbortSignal(
      externalSignal,
      timeout,
    );

    try {
      const authAndUserHeaders = await this.prepareAuthHeaders(options);

      const clientRequestOptionsObj: any = {
        document: document,
        requestHeaders: authAndUserHeaders as HeadersInit,
        signal: operationSignal,
      };

      if (variables !== undefined) {
        clientRequestOptionsObj.variables = variables;
      }

      const typedOptions = clientRequestOptionsObj as LibGraphQLRequestOptions<
        TVars, // Variables type for the library
        TResult // Result type for the library
      >;

      return await this.executeGraphQLRequest<TResult, TVars>(typedOptions);
    } catch (error: any) {
      throw this.handleGraphQLError(error, operationSignal);
    } finally {
      clearInternalTimeout();
    }
  }

  /**
   * Executes the actual REST request using the configured Axios instance.
   * @private
   */
  private async executeRestRequest<TData>(
    axiosConfig: AxiosRequestConfig,
  ): Promise<AxiosResponse<TData>> {
    return this.axiosInstance.request<TData>(axiosConfig);
  }

  /**
   * Processes a successful standard (non-raw) REST response into the customFetch-compatible format.
   * @private
   */
  private processStandardRestResponse<TData>(
    response: AxiosResponse<TData>,
  ): StandardRestResponse<TData> {
    const responseData = response.data;
    const success = response.status >= 200 && response.status < 300;

    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      !Array.isArray(responseData)
    ) {
      return {
        ...(responseData as object),
        success,
        statusCode: response.status,
      } as StandardRestResponse<TData>;
    } else {
      return {
        _rawData: responseData,
        success,
        statusCode: response.status,
      } as StandardRestResponse<TData>;
    }
  }

  /**
   * Handles errors from REST requests, transforming them into custom ApiError types.
   * This method always throws.
   * @private
   * @returns {never}
   */
  private handleRestError(
    error: any,
    fullUrl: string,
    operationSignal?: AbortSignal,
  ): never {
    if (operationSignal?.aborted) {
      throw new TimeoutError(
        typeof operationSignal.reason === 'string'
          ? operationSignal.reason
          : `REST request to '${fullUrl}' aborted.`,
        error,
      );
    }
    if (axios.isAxiosError(error)) {
      if (
        error.code === 'ECONNABORTED' ||
        error.message.toLowerCase().includes('timeout') ||
        (error.code && ['ETIMEDOUT', 'ESOCKETTIMEDOUT'].includes(error.code))
      ) {
        throw new TimeoutError(
          `REST request to '${fullUrl}' timed out. (${error.message})`,
          error,
        );
      }
      if (!error.response) {
        // No response from server
        throw new NetworkError(
          `Network error for REST request to '${fullUrl}': ${error.message}`,
          error,
        );
      }
      // Server responded with an error status
      throw new RestApiError(
        error.response.data?.message ||
          error.response.data?.error ||
          error.message ||
          'REST request failed',
        {
          status: error.response.status,
          data: error.response.data,
          headers: normalizeAxiosHeaders(error.response.headers),
        },
        error,
      );
    }
    // Fallback for unexpected errors
    throw new ApiError(
      `Unexpected error during REST request to '${fullUrl}': ${
        error.message || 'Unknown error'
      }`,
      error,
    );
  }

  /**
   * Executes the actual GraphQL request using the configured GraphQLClient instance.
   * @private
   */
  private async executeGraphQLRequest<
    TResult,
    TVariables extends LibGraphQLVariables,
  >(options: LibGraphQLRequestOptions<TVariables, TResult>): Promise<TResult> {
    return this.graphqlClient.request<TResult, TVariables>(options);
  }

  /**
   * Handles errors from GraphQL requests, transforming them into custom ApiError types.
   * This method always throws.
   * @private
   * @returns {never}
   */
  private handleGraphQLError(error: any, operationSignal?: AbortSignal): never {
    if (operationSignal?.aborted) {
      throw new TimeoutError(
        typeof operationSignal.reason === 'string'
          ? operationSignal.reason
          : 'GraphQL request aborted.',
        error,
      );
    }
    if (error instanceof GraphQLClientError) {
      const errorMsg =
        error.response?.errors?.[0]?.message ||
        error.message ||
        'GraphQL request failed';
      throw new GraphQLApiError(
        errorMsg,
        {
          status: error.response?.status,
          data: error.response?.data,
          errors: error.response?.errors,
        },
        error,
      );
    }
    if (
      error instanceof TypeError && // Network errors in fetch-based clients often manifest as TypeErrors
      (error.message.toLowerCase().includes('failed to fetch') ||
        error.message.toLowerCase().includes('networkerror'))
    ) {
      throw new NetworkError(
        `Network error during GraphQL request: ${error.message}`,
        error,
      );
    }
    // Fallback for unexpected errors
    throw new ApiError(
      `Unexpected error during GraphQL request: ${
        error.message || 'Unknown error'
      }`,
      error,
    );
  }

  /**
   * Retrieves the JWT authentication token using the injected `getAuthTokenFn`.
   * @private
   * @returns {Promise<string | null>} The JWT token or null if unavailable/error.
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      return await this.getAuthTokenFn();
    } catch (error: any) {
      this.logger.error(
        'Error retrieving auth token via getAuthTokenFn:',
        error.message,
        error,
      );
      return null;
    }
  }

  /**
   * Prepares request headers, primarily for authentication.
   * It merges existing headers with authentication headers if `secure` is true.
   * @private
   * @param options Configuration for header preparation, including security flags and existing headers.
   * @returns {Promise<Record<string, string>>} The prepared headers.
   */
  private async prepareAuthHeaders(
    options: Pick<RestRequestConfig, 'secure' | 'account'> & {
      // Re-using RestRequestConfig for common fields
      headers?: Record<string, string> | HeadersInit;
    },
  ): Promise<Record<string, string>> {
    const preparedHeaders: Record<string, string> = {};
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          preparedHeaders[key] = value;
        });
      } else {
        // Ensure it's a plain object and values are strings
        for (const key in options.headers) {
          if (Object.prototype.hasOwnProperty.call(options.headers, key)) {
            // existingHeaders here is Record<string, string> or object part of HeadersInit
            const value = (options.headers as Record<string, any>)[key];
            if (typeof value === 'string') {
              preparedHeaders[key] = value;
            }
          }
        }
      }
    }

    if (options.secure) {
      const token = await this.getAuthToken();
      if (token) {
        preparedHeaders['Authorization'] = `Bearer ${token}`;
        if (options.account) {
          preparedHeaders['ls-account'] = options.account;
        }
      } else {
        this.logger.warn(
          'Secure request: Auth token not available or getAuthTokenFn failed.',
        );
      }
    }
    return preparedHeaders;
  }

  /**
   * Creates and manages a combined AbortSignal for request timeout and external cancellation.
   * Uses `createCombinedSignal` utility.
   * @private
   * @param externalSignal An optional AbortSignal provided by the caller.
   * @param requestTimeout The timeout duration for this specific request.
   * @returns An object containing the `operationSignal` to be used for the request,
   * and a `clearInternalTimeout` function to clear any internal timers.
   */
  private createAbortSignal(
    externalSignal?: AbortSignal,
    requestTimeout?: number,
  ): { operationSignal?: AbortSignal; clearInternalTimeout: () => void } {
    const effectiveTimeout = requestTimeout ?? this.defaultTimeout;
    let internalTimeoutController: AbortController | undefined;
    let timeoutId: NodeJS.Timeout | undefined; // For Node.js environments; browser uses number

    if (effectiveTimeout > 0) {
      internalTimeoutController = new AbortController();
      timeoutId = setTimeout(() => {
        internalTimeoutController!.abort(
          'Request timed out by ApiClient internal timeout',
        );
      }, effectiveTimeout);
    }

    const clearInternalTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
    };

    const operationSignal = createCombinedSignal([
      externalSignal,
      internalTimeoutController?.signal,
    ]);

    // If the combined signal is already aborted (e.g., externalSignal was pre-aborted),
    // ensure our internal timeout is cleared.
    if (operationSignal?.aborted) {
      clearInternalTimeout();
    } else {
      // Otherwise, if the combined signal aborts for any reason later, clear the timeout.
      operationSignal?.addEventListener('abort', clearInternalTimeout, {
        once: true,
      });
    }

    return { operationSignal, clearInternalTimeout };
  }
}
